import type { ComponentType } from 'react'

// A module that exports a prop-less page component under `exportName`.
type PageModule<K extends string> = Record<K, ComponentType>

/**
 * React Router v7's granular `lazy` object form, defers a route's component
 * to a dynamic import. `path`, `children`, and `handle` stay on the static
 * route object — react-router cannot lazy `path`/`children` at all, and
 * `handle` must stay eager because `FeatureGuardLayout` gates on
 * `handle.featureKey` and `useRouteSentryTransaction` names the transaction
 * from `handle.sentryTransactionPath` before the chunk resolves.
 *
 * A route carrying `lazy` must NOT also carry `element` — react-router logs a
 * warning and renders the static `element`, silently skipping the split.
 *
 * Call this once PER ROUTE. React Router mutates the returned object while
 * resolving it, and shares route objects by reference when a route table
 * spreads the same call result into two routes — so two routes built from one
 * shared `lazy` object leave the second with no component and no element (a
 * blank page, no warning). When several routes share a container, wrap the
 * call in a local factory and invoke it at each call site instead of hoisting
 * a single result:
 *
 *   const alertsPage = () => lazyDefault(() => import('features/alerts/Container'))
 *   export const alertsRoutes: RouteObject[] = [
 *     { path: ALERTS_URL, lazy: alertsPage(), handle: { ... } },
 *     { path: ALERTS_DETAIL_PATH, lazy: alertsPage(), handle: { ... } },
 *   ]
 */
export const lazyComponent = <K extends string>(
  load: () => Promise<PageModule<K>>,
  exportName: K,
): { Component: () => Promise<ComponentType> } => ({
  Component: () => load().then((mod) => mod[exportName]),
})

/** `lazyComponent` for a module whose page is the default export. */
export const lazyDefault = (
  load: () => Promise<{ default: ComponentType }>,
): { Component: () => Promise<ComponentType> } => ({
  Component: () => load().then((mod) => mod.default),
})
